import client from '@/shared/apis/client'
import axios from 'axios'
import {
  API_TIMEOUT_CHUNK_MS,
  API_TIMEOUT_UPLOAD_MS,
} from '@/shared/apis/timeouts'
import { type SessionType } from '@/shared/types/yakurekiCore'

// ========================================
// Types (yakureki-back PR #1053 に対応)
// 参照: yakureki-back/log/144/work-log/17 §5 streaming API spec
// ========================================

export type StreamStartRequest = {
  yakureki_ids: string[]
  pharmacist_id?: string
  memo_items?: string[]
  /**
   * 対象患者の家族 (同伴/代理) 有無を event ごとに指定する。
   * SOAP 生成 prompt の `# 登場する話者の可能性` 振り分け規則テーブルに
   * 「対象患者の家族 (同伴/代理)」行が露出する (yakureki-back log/145/work-log/01)。
   * 省略時は全 event False (家族行なし)。
   */
  has_companion_or_substitute_map?: Record<string, boolean>
  /**
   * 録音時プリセット (標準=auto / 深掘り=deep)。
   * 深掘りは生成時に賢いモデル + 過去履歴を常に参照する。auto の注入可否は back が
   * 生成時 (finalize 後・最終 transcript) に評価する。省略時 back default auto。
   */
  history_policy?: 'auto' | 'deep'
  /**
   * フリー録音 mode. true なら yakureki_ids 空必須、 back が空 Event 1 件を auto-create.
   * response.yakureki_ids[0] が auto-create された Event id.
   */
  is_free_recording?: boolean
  /** フリー録音時の label override. 省略時 back が 'Meeting YYYY-MM-DD HH:MM' で自動採番. */
  label?: string
  /**
   * 来局/在宅の別。フリー録音 (is_free_recording=true) のみ参照される。省略時 back default walk_in。
   * 患者紐付け録音の session_type は既存 event の属性なので PATCH session-type が唯一の変更経路。
   */
  session_type?: SessionType
  client_meta?: Record<string, unknown>
}

export type StreamStartResponse = {
  session_id: string
  recording_session_id: string
  yakureki_ids: string[]
  stream_endpoint: string
  finalize_endpoint: string
}

export type StreamChunkAck = {
  received_seq: number
}

/**
 * finalize response の文字起こしモード.
 * - streaming:      Pass 1 (Chirp streaming transcript) から SOAP 生成が走る (通常).
 * - batch_fallback: backend が legacy batch (低速) で SOAP を再生成する
 *                   (SS=FAILED 由来の GS-3 / finalize 時の chunk 欠落検知 chunk_audio_gap).
 * 旧 backend は field 自体を返さない (undefined) ため optional.
 */
export type TranscriptionMode = 'streaming' | 'batch_fallback'

export type StreamFinalizeResponse = {
  // 'cancelled' は salvage finalize (log/185: client_fallback 降格後の遅延 commit) の応答.
  // SS は CANCELLED のまま audio 永続化 + GS-3 が走る (transcription_mode='batch_fallback').
  status: 'finalized' | 'failed' | 'cancelled'
  recording_session_id: string
  yakureki_ids: string[]
  transcription_mode?: TranscriptionMode
}

export type StreamingSessionStatus =
  | 'started'
  | 'recording'
  | 'finalized'
  | 'failed'

export type StreamingSessionDetail = {
  id: string
  status: StreamingSessionStatus
  transcript: {
    finals: Array<{
      seq: number
      audio_end_s: number
      text: string
      conf: number
      wall_ts: string
    }>
    interim: {
      audio_end_s: number
      text: string
      wall_ts: string
    } | null
    updated_at: string | null
  }
  started_at: string | null
  finalized_at: string | null
  error: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

// ========================================
// Errors
// ========================================

/**
 * stream/chunks や stream/finalize が 409 を返した場合に投げる。
 * sticky session の失敗 or ECS task restart で runner_registry から session が消えたケース。
 * caller 側は本エラーを検知して MediaRecorder fallback に切り替える。
 */
export class StreamingSessionNotFoundError extends Error {
  constructor(public readonly sessionId: string) {
    super(
      `streaming session ${sessionId} not found on this task (sticky failed)`
    )
    this.name = 'StreamingSessionNotFoundError'
  }
}

const is409 = (err: unknown): boolean => {
  return axios.isAxiosError(err) && err.response?.status === 409
}

/**
 * finalize / fallback commit の失敗を UI 向けに正規化した error。
 *
 * message はそのまま RecordingFailedAlert に表示できる日本語文言。
 * isRetryHopeless=true は「同じ blob の再送信では回復する見込みが薄い」失敗
 * (= session 消滅 409 / server が判断を下した 4xx) を表し、 UI は再試行ボタンを隠して
 * [音声をダウンロード] 誘導に切り替える。 network 断 / 5xx は再試行に意味があるため false。
 */
export class RecordingSubmitFailedError extends Error {
  readonly isRetryHopeless: boolean

  constructor(message: string, isRetryHopeless: boolean) {
    super(message)
    this.name = 'RecordingSubmitFailedError'
    this.isRetryHopeless = isRetryHopeless
  }
}

/**
 * finalize / fallback commit の catch で受けた raw error を、 ユーザー向け文言を持つ
 * RecordingSubmitFailedError に変換する。 axios の "Request failed with status code 400"
 * のような生文言を UI に出さないための一元変換点。
 * back が response body に日本語の具体理由 (`{error: "音声が20分を超えています…"}` 等) を
 * 返す場合はそれを優先して表示する (= ユーザーが原因を自己判別できる)。
 * 分類不能な Error はそのまま返す (= 未知の JS error は原文を残して調査可能性を優先)。
 */
export const toRecordingSubmitFailedError = (err: unknown): Error => {
  if (err instanceof StreamingSessionNotFoundError) {
    return new RecordingSubmitFailedError(
      '録音セッションが既に閉じられているため再試行できません。 [音声をダウンロード] でファイルを保存し、別経路で投稿してください。',
      true
    )
  }
  if (axios.isAxiosError(err)) {
    const status = err.response?.status
    const serverMessage =
      typeof (err.response?.data as { error?: unknown } | undefined)?.error ===
      'string'
        ? ((err.response?.data as { error: string }).error as string)
        : null
    if (status === undefined) {
      return new RecordingSubmitFailedError(
        '通信エラーで録音データを送信できませんでした。 通信環境を確認して再試行してください。',
        false
      )
    }
    if (status >= 400 && status < 500) {
      return new RecordingSubmitFailedError(
        serverMessage
          ? `${serverMessage} 再試行では解決しないため、 [音声をダウンロード] でファイルを保存してください。`
          : `録音データがサーバーに受け付けられませんでした (エラー ${status})。 再試行では解決しない可能性が高いため、 [音声をダウンロード] でファイルを保存してください。`,
        true
      )
    }
    return new RecordingSubmitFailedError(
      serverMessage
        ? `${serverMessage} しばらくおいて再試行してください。`
        : `サーバーエラーで録音データを送信できませんでした (エラー ${status})。 しばらくおいて再試行してください。`,
      false
    )
  }
  return err instanceof Error ? err : new Error(String(err))
}

/**
 * chunk POST 失敗が「瞬断 (= 1 回だけ再送する価値あり)」かを判定する。
 *
 * - HTTP 応答なし (network 断 / timeout=ECONNABORTED) のみ transient = true。
 *   back が受領済なら seq dedup で無視され、 未達なら処理されるので再送が冪等。
 * - 409 (session/runner 真死) は同 task に戻れないため fatal = false。
 * - 4xx/5xx (応答あり) は server が判断を下しているため再送せず fatal = false。
 * - それ以外 (未知) は安全側に倒して fatal = false (= 即 fallback)。
 */
export const isTransientChunkError = (err: unknown): boolean => {
  if (err instanceof StreamingSessionNotFoundError) return false
  if (typeof err !== 'object' || err === null) return false
  if (axios.isAxiosError(err)) return !err.response
  return false
}

// ========================================
// API functions
// ========================================

/**
 * POST /api/yakureki/stream/start/
 *
 * StreamingSession を新規作成し、ALB sticky 配下の 1 ECS task 上で
 * ChirpSessionRunner を start する。以降のリクエストは必ず同じ task に届く必要がある
 * (AWSALB cookie ベース)。
 */
export const startStreamingSession = async (
  params: StreamStartRequest
): Promise<StreamStartResponse> => {
  const response = await client.post<StreamStartResponse>(
    '/api/yakureki/stream/start/',
    params
  )
  return response.data
}

/**
 * POST /api/yakureki/stream/{session_id}/chunks/
 *
 * 100ms 分の LINEAR16 PCM を base64 で送る。
 * seq は 0 から単調増加。409 は sticky 失敗 → fallback トリガ。
 */
export const postAudioChunk = async (
  sessionId: string,
  seq: number,
  audioBase64: string
): Promise<StreamChunkAck> => {
  try {
    const response = await client.post<StreamChunkAck>(
      `/api/yakureki/stream/${sessionId}/chunks/`,
      { seq, audio_base64: audioBase64 },
      { timeout: API_TIMEOUT_CHUNK_MS }
    )
    return response.data
  } catch (err) {
    if (is409(err)) throw new StreamingSessionNotFoundError(sessionId)
    throw err
  }
}

/**
 * POST /api/yakureki/stream/{session_id}/finalize/  (multipart audio 同梱)
 *
 * runner に SENTINEL を送り、Chirp iterator を閉じて transcript を確定させる。
 * 同時に MediaRecorder で並列録音した全体音声 (audioBlob) を multipart で
 * 同梱し、 backend 側で persist_stream_audio (S3 + GCS + post_batch kick) を
 * daemon thread で実行させる。 backend は finalize 後 process_streaming_to_soap も
 * async kick するため、 1 endpoint で SOAP 生成 + 音声永続化の両方が走る (1-signal 化)。
 *
 * audioBlob は省略可 (capture 失敗等の defensive case): その場合は backend 側で
 * 音声永続化は skip され、 Pass 1 SOAP 生成のみ走る。
 *
 * captureSettings は録音開始時に取った収音の実効値 snapshot (track.getSettings())。
 * stream/start は mic 取得前に呼ばれるため実効値を載せられず、 運搬は finalize が担う
 * (log/207 F。取得時点は録音開始・配達が遅いだけで snapshot の意味は変わらない)。
 * 省略可・旧 backend は field を無視する。
 */
export const finalizeStream = async (
  sessionId: string,
  audioBlob?: Blob,
  captureSettings?: Record<string, unknown>
): Promise<StreamFinalizeResponse> => {
  try {
    const formData = new FormData()
    if (audioBlob) {
      // backend は拡張子で mime 判定しないため extension は目印程度で OK
      formData.append('audio', audioBlob, 'audio.mp4')
    }
    if (captureSettings) {
      formData.append('capture_settings', JSON.stringify(captureSettings))
    }
    const response = await client.post<StreamFinalizeResponse>(
      `/api/yakureki/stream/${sessionId}/finalize/`,
      formData,
      { timeout: API_TIMEOUT_UPLOAD_MS }
    )
    return response.data
  } catch (err) {
    if (is409(err)) throw new StreamingSessionNotFoundError(sessionId)
    throw err
  }
}

export type StreamPauseResponse = {
  status: 'paused'
  segment_index: number
  next_segment_index: number
  staged_duration_s: number
}

export type StreamResumeResponse = {
  status: 'active' | 'already_active'
  segment_index?: number
  stream_endpoint?: string
  finalize_endpoint?: string
}

/**
 * POST /api/yakureki/stream/{session_id}/pause/  (multipart audio 同梱)
 *
 * 一時停止 (log/185 セグメント確定方式): 現時点までの並列録音 blob を 1 セグメントとして
 * back に確定させる (Pass 1 flush + StreamingSegment staging 保存。SOAP は蹴らない)。
 * 200 受領後は front 側のマイク・blob・エンジンを全解放してよい (paused 状態の正本は
 * サーバの SS.status=PAUSED)。失敗時は blob を保持して同 segment_index で再送すれば
 * 冪等に回復する (back は update_or_create で upsert)。
 */
export const pauseStreamingSession = async (
  sessionId: string,
  audioBlob: Blob,
  segmentIndex: number
): Promise<StreamPauseResponse> => {
  try {
    const formData = new FormData()
    formData.append('audio', audioBlob, 'segment.mp4')
    formData.append('segment_index', String(segmentIndex))
    const response = await client.post<StreamPauseResponse>(
      `/api/yakureki/stream/${sessionId}/pause/`,
      formData,
      { timeout: API_TIMEOUT_UPLOAD_MS }
    )
    return response.data
  } catch (err) {
    if (is409(err)) throw new StreamingSessionNotFoundError(sessionId)
    throw err
  }
}

/**
 * POST /api/yakureki/stream/{session_id}/resume/
 *
 * 一時停止からの再開。back は PAUSED→ACTIVE + 新 runner (確定済みセグメント duration 合計を
 * initial_offset_s に受ける) を起動する。累計録音時間が上限 (RECORDING_MAX_S) に達している
 * 場合は 409 で拒否される (= 完了を促す)。
 */
export const resumeStreamingSession = async (
  sessionId: string
): Promise<StreamResumeResponse> => {
  const response = await client.post<StreamResumeResponse>(
    `/api/yakureki/stream/${sessionId}/resume/`
  )
  return response.data
}

/**
 * cancel の provenance タグ (back の error.reason に記録される)。
 * back の allowlist (streaming_constants.resolve_cancel_reason) と揃える。
 * - user_cancelled:     録音中断ボタン等、ユーザーの意図的キャンセル (既定)
 * - client_fallback:    低速 (MediaRecorder) fallback 切替で streaming を放棄したとき。
 *                       backend runner を graceful close し、 chunk 途絶 → Google ABORT
 *                       由来の `ChirpSessionRunner failed` ログ汚染を未然に防ぐ。
 * - client_interrupted: 回復不能中断時の cancel 用 (back allowlist と共有する語彙)。
 *                       本 web は中断時に finalizeStream で SOAP を保全する経路に倒すため
 *                       未使用だが、 quickrec 等の別 client / 将来用に型として保持する。
 */
export type CancelReason =
  | 'user_cancelled'
  | 'client_fallback'
  | 'client_interrupted'

export type CancelStreamResult = {
  /**
   * back が削除したフリー録音の event id (ADR-0039)。一覧の delta 同期は updated_at 起点で
   * hard delete を表現できないため、呼び出し側が名指しで一覧から外す。
   */
  deleted_event_ids: string[]
}

/**
 * POST /api/yakureki/stream/{session_id}/cancel/
 *
 * runner を graceful close + SS=CANCELLED(reason)。finalize と異なり SOAP pipeline は
 * kick されず、同じ event で再録音できるよう event.status を RECORDING → PENDING に戻す。
 * フリー録音の器は削除され、その id が deleted_event_ids で返る。
 * reason は出自タグ (既定 'user_cancelled')。 back 側で allowlist 検証される。
 */
export const cancelStream = async (
  sessionId: string,
  reason: CancelReason = 'user_cancelled'
): Promise<CancelStreamResult> => {
  try {
    const res = await client.post<{ deleted_event_ids?: string[] }>(
      `/api/yakureki/stream/${sessionId}/cancel/`,
      { reason }
    )
    return { deleted_event_ids: res.data?.deleted_event_ids ?? [] }
  } catch (err) {
    if (is409(err)) throw new StreamingSessionNotFoundError(sessionId)
    throw err
  }
}

/**
 * GET /api/yakureki/stream/{session_id}/
 *
 * interim / finals の現在状態を取得する。streaming 中のライブ表示に使う。
 */
export const retrieveStream = async (
  sessionId: string
): Promise<StreamingSessionDetail> => {
  const response = await client.get<StreamingSessionDetail>(
    `/api/yakureki/stream/${sessionId}/`
  )
  return response.data
}

// ========================================
// Helpers
// ========================================

/**
 * ArrayBuffer (LINEAR16 PCM) を base64 string に変換する。
 * 100ms chunk ≈ 3200 bytes なので btoa + String.fromCharCode で十分 (1 MB 以下で安全)。
 */
export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}
